import React from 'react';
import { Box } from '@mui/material';
import {
  signalAvatarTones,
  signalBorders,
  signalFonts,
  signalRadii,
  signalShadows,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  DawTile,
  InitialsAvatar,
  Mono,
  ScreenFrame,
  SignalButton,
  StatusPill,
  Waveform,
} from '@/common/components/signal';
import { columns } from '../fixtures';
import type { BoardCardFixture } from '../fixtures';

/** Dashed placeholder — "＋ Add project" in a column, "＋ Add column" at the end. */
const AddSlot: React.FC<{
  children: React.ReactNode;
  radius: number;
  padding: string;
  border: string;
  sx?: object;
}> = ({ children, radius, padding, border, sx }) => (
  <Box
    sx={{
      padding,
      borderRadius: `${radius}px`,
      border: `1px dashed ${border}`,
      color: signalText.muted,
      fontSize: '12px',
      textAlign: 'center',
      cursor: 'pointer',
      '&:hover': {
        color: signalText.primary,
        borderColor: 'rgba(255,255,255,0.25)',
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * A project on the board.
 *
 * The drag state is a property of the card rather than a separate component:
 * lifting it swaps three values — border to accent, fill to a warm near-black,
 * shadow to the deep one — and adds a slight rotate/scale. Keeping them together
 * is what makes the card read as picked *up* rather than merely highlighted.
 */
const BoardCard: React.FC<{ card: BoardCardFixture }> = ({ card }) => (
  <Box
    sx={{
      border: `1px solid ${
        card.dragging ? 'rgba(255,106,31,0.55)' : signalBorders.default
      }`,
      borderRadius: '13px',
      background: card.dragging ? 'rgba(30,24,20,0.95)' : signalSurfaces.fill,
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '9px',
      boxShadow: card.dragging
        ? signalShadows.dragging
        : signalShadows.cardFlat,
      transform: card.dragging ? 'rotate(-1.6deg) scale(1.02)' : 'none',
      cursor: 'pointer',
      '&:hover': { borderColor: signalBorders.accentHover },
    }}
  >
    <Box sx={{ display: 'flex', gap: '9px', alignItems: 'flex-start' }}>
      <DawTile src={card.logo} label={card.daw} size={26} radius={7} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontWeight: 600,
            fontSize: '13px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {card.name}
        </Box>
        <Mono size={10} sx={{ display: 'block', marginTop: '2px' }}>
          {card.meta}
        </Mono>
      </Box>
    </Box>

    <Waveform bars={card.wave} height={22} />

    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
      <StatusPill status={card.tagStatus} size={9} borderless>
        {card.tag}
      </StatusPill>
      <InitialsAvatar
        initials={card.avatar.initials}
        bg={card.avatar.bg}
        fg={card.avatar.fg}
        size={20}
        sx={{ marginLeft: 'auto' }}
      />
    </Box>
  </Box>
);

/**
 * 05 — Collaboration board.
 *
 * A kanban over projects rather than tasks, so each card still carries its DAW,
 * version and waveform — the same identity it has in the grid. One card is shown
 * mid-drag because the board's whole purpose is moving work between stages, and
 * a static mock that never shows the motion hides its most important state.
 */
export const Screen05Board: React.FC = () => (
  <ScreenFrame
    index="05"
    title="Collaboration board"
    note="KANBAN · DRAG STATE SHOWN"
    frameSx={{ padding: '24px 26px 30px' }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        marginBottom: '22px',
      }}
    >
      <Box>
        <Mono size={11} sx={{ display: 'block', marginBottom: '6px' }}>
          BOARDS / ALBUM
        </Mono>
        <Box
          component="h3"
          sx={{
            margin: 0,
            fontFamily: signalFonts.display,
            fontWeight: 800,
            fontSize: '32px',
            letterSpacing: '-0.03em',
            lineHeight: 1,
          }}
        >
          Autocannon — LP1
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Ringed in the screen background so the stack reads as overlapping
            discs rather than as one blurred shape. */}
        <Box sx={{ display: 'flex' }}>
          {[
            { initials: 'MK', tone: signalAvatarTones.accent },
            { initials: 'JD', tone: signalAvatarTones.cyan },
            { initials: 'AR', tone: signalAvatarTones.green },
          ].map((p, i) => (
            <InitialsAvatar
              key={p.initials}
              initials={p.initials}
              bg={p.tone.bg}
              fg={p.tone.fg}
              size={26}
              ring={signalSurfaces.screen}
              sx={{ marginLeft: i === 0 ? 0 : '-8px' }}
            />
          ))}
        </Box>
        <SignalButton variant="secondary">Invite</SignalButton>
        <SignalButton>＋ Add project</SignalButton>
      </Box>
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr) 200px',
        gap: '16px',
        alignItems: 'start',
      }}
    >
      {columns.map((col) => (
        <Box
          key={col.name}
          sx={{
            border: `1px solid ${signalBorders.subtle}`,
            borderRadius: `${signalRadii['2xl']}px`,
            background: signalSurfaces.fillSubtle,
            padding: '14px 12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '0 4px',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 7,
                height: 7,
                flex: 'none',
                borderRadius: '999px',
                background: col.dot,
              }}
            />
            <Mono
              size={11}
              weight={600}
              letterSpacing="0.1em"
              color={signalText.primary}
            >
              {col.name}
            </Mono>
            <Mono size={11}>{col.count}</Mono>
            <Box
              component="span"
              sx={{
                marginLeft: 'auto',
                color: signalText.faint,
                fontSize: '13px',
              }}
            >
              ⋮
            </Box>
          </Box>

          {col.cards.map((card) => (
            <BoardCard key={card.name} card={card} />
          ))}

          <AddSlot radius={11} padding="9px 10px" border={signalBorders.strong}>
            ＋ Add project
          </AddSlot>
        </Box>
      ))}

      <AddSlot
        radius={signalRadii['2xl']}
        padding="16px 12px"
        border={signalBorders.stronger}
        sx={{
          fontSize: '12.5px',
          minHeight: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            color: signalText.primary,
            borderColor: 'rgba(255,255,255,0.28)',
          },
        }}
      >
        ＋ Add column
      </AddSlot>
    </Box>
  </ScreenFrame>
);

export default Screen05Board;
